import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { leadService } from '../services/leadService';
import PhoneSearch from './PhoneSearch';
import AddLeadForm from './AddLeadForm';
import LeadsList from './LeadsList';
import SettingsModal from './SettingsModal';

const { FiUsers, FiPhone, FiUserCheck, FiTrendingUp, FiDatabase, FiAlertCircle, FiClock, FiSettings, FiLogOut } = FiIcons;

const Dashboard = ({ onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      setIsInitialLoading(true);
      setError(null);
      console.log('Loading leads...');
      const leadsData = await leadService.getAllLeads();
      setLeads(leadsData);
      console.log('Leads loaded successfully:', leadsData);
    } catch (error) {
      console.error('Error loading leads:', error);
      setError('Failed to load leads. Please check your Firebase configuration.');
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handlePhoneSearch = async (phoneNumber) => {
    try {
      setIsLoading(true);
      setError(null);
      const results = await leadService.searchByPhone(phoneNumber);
      setSearchResult(results);
    } catch (error) {
      console.error('Error searching phone:', error);
      setError('Failed to search phone number.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLead = async (leadData) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Adding lead:', leadData);
      const newLead = await leadService.addLead({
        ...leadData,
        callHistory: [],
        lastContact: null
      });
      setLeads(prev => [newLead, ...prev]);
      console.log('Lead added successfully:', newLead);
    } catch (error) {
      console.error('Error adding lead:', error);
      setError('Failed to add lead. Please check your Firebase configuration and permissions.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLead = async (leadId, updateData) => {
    try {
      setError(null);
      await leadService.updateLead(leadId, updateData);
      setLeads(prev => prev.map(lead => lead.id === leadId ? { ...lead, ...updateData } : lead));
    } catch (error) {
      console.error('Error updating lead:', error);
      setError('Failed to update lead.');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        setError(null);
        await leadService.deleteLead(leadId);
        setLeads(prev => prev.filter(lead => lead.id !== leadId));
      } catch (error) {
        console.error('Error deleting lead:', error);
        setError('Failed to delete lead.');
      }
    }
  };

  const getStats = () => {
    const total = leads.length;
    const newLeads = leads.filter(lead => lead.status === 'new').length;
    const converted = leads.filter(lead => lead.status === 'converted').length;
    const contacted = leads.filter(lead => lead.status === 'contacted').length;
    const totalCalls = leads.reduce((sum, lead) => sum + (lead.callHistory?.length || 0), 0);

    return { total, newLeads, converted, contacted, totalCalls };
  };

  const handleLogout = () => {
    // Remove authentication from local storage
    localStorage.removeItem('lead_management_authenticated');
    
    // Call the logout callback
    if (onLogout) onLogout();
  };

  const stats = getStats();

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiDatabase} className="text-4xl text-blue-500 mb-4 animate-pulse" />
          <p className="text-gray-600">Loading your leads database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex justify-between items-center"
        >
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Lead Management Dashboard</h1>
            <p className="text-gray-600">Track and manage your leads efficiently</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setShowSettings(true)}
              className="px-3 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-1"
            >
              <SafeIcon icon={FiSettings} />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-2 bg-white text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-1"
            >
              <SafeIcon icon={FiLogOut} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </motion.div>

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiAlertCircle} className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
            <p className="text-red-600 text-sm mt-2">
              Please check the browser console for more details.
            </p>
          </motion.div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <SafeIcon icon={FiUsers} className="text-blue-500 text-2xl" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Leads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.newLeads}</p>
              </div>
              <SafeIcon icon={FiPhone} className="text-green-500 text-2xl" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Contacted</p>
                <p className="text-2xl font-bold text-gray-900">{stats.contacted}</p>
              </div>
              <SafeIcon icon={FiUserCheck} className="text-yellow-500 text-2xl" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Converted</p>
                <p className="text-2xl font-bold text-gray-900">{stats.converted}</p>
              </div>
              <SafeIcon icon={FiTrendingUp} className="text-purple-500 text-2xl" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Calls</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCalls}</p>
              </div>
              <SafeIcon icon={FiClock} className="text-orange-500 text-2xl" />
            </div>
          </motion.div>
        </div>

        {/* Phone Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <PhoneSearch
            onSearch={handlePhoneSearch}
            searchResult={searchResult}
            isLoading={isLoading}
          />
        </motion.div>

        {/* Add Lead Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <AddLeadForm onAddLead={handleAddLead} isLoading={isLoading} />
        </motion.div>

        {/* Leads List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <LeadsList
            leads={leads}
            onUpdateLead={handleUpdateLead}
            onDeleteLead={handleDeleteLead}
          />
        </motion.div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
};

export default Dashboard;